import React from 'react';
import { X, Music, Tag, Calendar, User } from 'lucide-react';

const TrackDetailModal = ({ track, isOpen, onClose, user }) => {
  if (!isOpen || !track) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending':
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

           return (
           <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="relative bg-black/90 border border-white/20 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-[0_4px_4px_rgba(0,0,0,0.17)]">
        <div className="sticky top-0 bg-black/90 border-b border-white/20 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">{track.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Track Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <span className="text-white">Genre: {track.genre || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Music className="h-4 w-4 text-gray-400" />
                    <span className="text-white">Duration: {track.duration || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white">BPM: {track.bpm || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white">Key: {track.song_key || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white">Release Package: {track.release_package || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white">ISRC: {track.isrc || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white">UPC: {track.upc || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-white">Language: {track.language || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Status & Metadata</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(track.status)}`}>
                      {track.status || 'pending'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-white">Submitted: {formatDate(track.created_at)}</span>
                  </div>
                  {track.updated_at && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-white">Last Updated: {formatDate(track.updated_at)}</span>
                    </div>
                  )}
                  {user?.role === 'admin' && (
                    <div className="flex items-center space-x-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-white">User: {track.user_email || 'Unknown User'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Artist Information */}
            {track.artist_name && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Artist Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-white">Artist: {track.artist_name}</span>
                  </div>
                  {track.featured_artist_name && (
                    <div className="flex items-center space-x-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-white">Featured Artist: {track.featured_artist_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional Details */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Additional Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-white font-medium">Lyrics: </span>
                  <span className="text-gray-300">{track.lyrics ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Lyrics Type: </span>
                  <span className="text-gray-300">{track.lyrics_type || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Composition Type: </span>
                  <span className="text-gray-300">{track.composition_type || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Description: </span>
                  <span className="text-gray-300">{track.song_description || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Producer: </span>
                  <span className="text-gray-300">{track.producer || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Engineer: </span>
                  <span className="text-gray-300">{track.engineer || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Mixer: </span>
                  <span className="text-gray-300">{track.mixer || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Mastering Engineer: </span>
                  <span className="text-gray-300">{track.mastering_engineer || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Recording Studio: </span>
                  <span className="text-gray-300">{track.recording_studio || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Mix Studio: </span>
                  <span className="text-gray-300">{track.mix_studio || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Mastering Studio: </span>
                  <span className="text-gray-300">{track.mastering_studio || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Recording Date: </span>
                  <span className="text-gray-300">{track.recording_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Mix Date: </span>
                  <span className="text-gray-300">{track.mix_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-white font-medium">Mastering Date: </span>
                  <span className="text-gray-300">{track.mastering_date || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Writer Information */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Writer Information</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <span className="text-white font-medium">Writer 1: {track.writer_1 || 'N/A'}</span>
                  <span className="text-gray-300">Split: {track.writer_1_split || 'N/A'}</span>
                  <span className="text-gray-300">PRO: {track.writer_1_pro || 'N/A'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <span className="text-white font-medium">Writer 2: {track.writer_2 || 'N/A'}</span>
                  <span className="text-gray-300">Split: {track.writer_2_split || 'N/A'}</span>
                  <span className="text-gray-300">PRO: {track.writer_2_pro || 'N/A'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <span className="text-white font-medium">Writer 3: {track.writer_3 || 'N/A'}</span>
                  <span className="text-gray-300">Split: {track.writer_3_split || 'N/A'}</span>
                  <span className="text-gray-300">PRO: {track.writer_3_pro || 'N/A'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <span className="text-white font-medium">Writer 4: {track.writer_4 || 'N/A'}</span>
                  <span className="text-gray-300">Split: {track.writer_4_split || 'N/A'}</span>
                  <span className="text-gray-300">PRO: {track.writer_4_pro || 'N/A'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <span className="text-white font-medium">Writer 5: {track.writer_5 || 'N/A'}</span>
                  <span className="text-gray-300">Split: {track.writer_5_split || 'N/A'}</span>
                  <span className="text-gray-300">PRO: {track.writer_5_pro || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Moods and Tags */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Moods & Tags</h3>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-sm">
                    {track.mood_1 || 'N/A'}
                  </span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-sm">
                    {track.mood_2 || 'N/A'}
                  </span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-sm">
                    {track.mood_3 || 'N/A'}
                  </span>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-sm">
                    {track.mood_4 || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-white font-medium">Tags: </span>
                  <span className="text-gray-300">{track.tags || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackDetailModal;
